import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import useBackHandler from '../hooks/useBackHandler';
import { readReviewDraft, saveReviewDraft } from '../utils/reviewDraft';
import { getScreenCacheContextRevision } from '../utils/localCache';
import { sheetSpacing } from '../design/tokens';
import SingleLineText from './ui/SingleLineText';
import Button from './ui/FitButton';
import LoadingLabel from './LoadingLabel';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, TextField, CircularProgress, Backdrop,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BuildIcon from '@mui/icons-material/Build';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import BottomSheet from './BottomSheet';
import { getDeviceUuid, getMyReviews, markReviewRead, submitReview } from '../api';

const CATEGORIES = [
    { value: 'IMPROVEMENT', label: translationKey('개선 제안'), icon: BuildIcon, color: '#3b82f6' },
    { value: 'FEATURE', label: translationKey('기능 요청'), icon: LightbulbIcon, color: '#f59e0b' },
    { value: 'BUG', label: translationKey('버그 신고'), icon: BugReportIcon, color: '#ef4444' },
];

function formatShortDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return localize("{v0}월 {v1}일", { v0: d.getMonth() + 1, v1: d.getDate() });
}

function categoryLabel(value) {
    return CATEGORIES.find(cat => cat.value === value)?.label || translationKey('기타');
}

function normalizeCategory(value) {
    return CATEGORIES.some(cat => cat.value === value) ? value : 'IMPROVEMENT';
}

function getReviewReplies(review) {
    if (!review) return [];
    if (Array.isArray(review.replies) && review.replies.length > 0) return review.replies;
    if (review.adminReply) {
        return [{
            id: `legacy-${review.id}`,
            content: review.adminReply,
            userReadAt: review.userReadAt,
            createdAt: review.adminRepliedAt,
        }];
    }
    return [];
}

function hasUnreadReplies(review) {
    return getReviewReplies(review).some(reply => !reply.userReadAt);
}

function countUnreadReplies(reviews) {
    return reviews.reduce((count, review) => count + getReviewReplies(review).filter(reply => !reply.userReadAt).length, 0);
}

function latestReplyText(review) {
    const replies = getReviewReplies(review);
    return replies[replies.length - 1]?.content || '';
}

function FollowUpContext({ review }) {
    const replies = getReviewReplies(review);
    return (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #eef2f7' }}>
            <SingleLineText sx={{ fontSize: 15, fontWeight: 900, color: '#1a1a1a', mb: 1.2 }}>{localize("이 대화에 추가 의견을 남겨요")}</SingleLineText>
            <Box sx={{ p: 1.25, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid #eef2f7', mb: 1 }}>
                <SingleLineText sx={{ fontSize: 12, fontWeight: 900, color: '#64748b', mb: 0.45 }}>{localize("처음 보낸 의견")}</SingleLineText>
                <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 700, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                    {review.content}
                </Typography>
            </Box>
            {replies.length > 0 ? replies.map((reply, index) => (
                <Box key={reply.id || `${review.id}-reply-${index}`} sx={{ p: 1.25, bgcolor: '#fffdf2', borderRadius: 2.5, border: '1px solid #fde68a', mt: index === 0 ? 0 : 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#8a7000', mb: 0.45 }}>
                        {replies.length > 1 ? localize("개발자 답변 {v0}", { v0: index + 1 }) : localize("개발자 답변")}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#1a1a1a', fontWeight: 750, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                        {reply.content}
                    </Typography>
                </Box>
            )) : (
                <Box sx={{ p: 1.25, bgcolor: '#fffdf2', borderRadius: 2.5, border: '1px solid #fde68a' }}>
                    <SingleLineText sx={{ fontSize: 12, fontWeight: 900, color: '#8a7000', mb: 0.45 }}>{localize("개발자 답변")}</SingleLineText>
                    <Typography sx={{ fontSize: 14, color: '#1a1a1a', fontWeight: 750, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                        {latestReplyText(review)}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function ReviewConversation({ review, emptyText = translationKey('답변이 오면 홈의 건의함에 알려드릴게요.'), compact = false }) {
    const replies = getReviewReplies(review);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 1 : 1.15 }}>
            <Box sx={{
                px: compact ? 1.3 : 1.55,
                py: compact ? 1.15 : 1.35,
                bgcolor: '#fff',
                border: '1px solid #eef2f7',
                borderRadius: 3,
            }}>
                <SingleLineText sx={{ fontSize: 12, fontWeight: 900, color: '#64748b', mb: 0.55 }}>{localize("보낸 의견")}</SingleLineText>
                <Typography sx={{
                    fontSize: compact ? 14 : 15,
                    color: '#1a1a1a',
                    fontWeight: 700,
                    lineHeight: 1.62,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'keep-all',
                }}>
                    {review.content}
                </Typography>
            </Box>
            {replies.length === 0 && (
                <Box sx={{
                    px: compact ? 1.3 : 1.55,
                    py: compact ? 1.15 : 1.35,
                    bgcolor: '#f9fafb',
                    border: '1px solid #eef2f7',
                    borderRadius: 3,
                }}>
                    <Typography sx={{ fontSize: compact ? 14 : 15, color: '#777', fontWeight: 750, lineHeight: 1.62, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                        {localize(emptyText)}
                    </Typography>
                </Box>
            )}
            {replies.map((reply, index) => (
                <Box key={reply.id || `${review.id}-${index}`} sx={{
                    px: compact ? 1.3 : 1.55,
                    py: compact ? 1.15 : 1.35,
                    bgcolor: '#fffdf2',
                    border: '1px solid #fde68a',
                    borderRadius: 3,
                }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#8a7000', mb: 0.55 }}>
                        {replies.length > 1 ? localize("답변 {v0}", { v0: index + 1 }) : localize("답변")}
                    </Typography>
                    <Typography sx={{
                        fontSize: compact ? 14 : 15,
                        color: '#1a1a1a',
                        fontWeight: 750,
                        lineHeight: 1.62,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'keep-all',
                    }}>
                        {reply.content}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function ReviewRow({ review, expanded, onClick }) {
    const hasUnreadReply = hasUnreadReplies(review);
    const hasReply = getReviewReplies(review).length > 0;
    const replyCount = getReviewReplies(review).length;
    return (
        <Box
            onClick={onClick}
            role="button"
            tabIndex={0}
            data-tappable="true"
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.4,
                py: 1.35, cursor: 'pointer',
                borderTop: '1px solid #f1f5f9',
                '&:active': { opacity: 0.72 },
            }}
        >
            <Box sx={{
                width: 38, height: 38, borderRadius: '12px',
                bgcolor: hasUnreadReply ? '#fff7cc' : '#f8fafc',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                {hasUnreadReply ? (
                    <MarkUnreadChatAltIcon sx={{ fontSize: 20, color: '#8a7000' }} />
                ) : (
                    <CheckCircleIcon sx={{ fontSize: 20, color: hasReply ? '#22c55e' : '#94a3b8' }} />
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: 15, fontWeight: 800, color: hasUnreadReply ? '#5a4a00' : '#1a1a1a',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {hasUnreadReply ? localize("답변 {v0}개가 도착했어요", { v0: replyCount }) : review.content}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: hasUnreadReply ? '#8a7000' : '#777', mt: 0.2 }}>
                    {hasReply ? localize("답변 {v0}개 · ", { v0: replyCount }) : ''}{localize(categoryLabel(review.category))} · {formatShortDate(review.createdAt)}
                </Typography>
            </Box>
            {hasUnreadReply && (
                <Box sx={{
                    width: 7, height: 7, borderRadius: '50%', bgcolor: '#f59e0b',
                    boxShadow: '0 0 0 4px rgba(245,158,11,0.12)', flexShrink: 0,
                }} />
            )}
            <ChevronRightIcon sx={{
                fontSize: 18,
                color: '#bbb',
                flexShrink: 0,
                transform: expanded ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.15s ease',
            }} />
        </Box>
    );
}

export default function ReviewSheet({ open, mode = 'default', onClose, onSubmitSuccess, onSubmitError, onUnreadChanged }) {
    const [ready, setReady] = useState(false);
    const loadVersion = useRef(0);
    const [category, setCategory] = useState('IMPROVEMENT');
    const [content, setContent] = useState('');
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [expandedReviewId, setExpandedReviewId] = useState(null);
    const [followUpReview, setFollowUpReview] = useState(null);
    const [readError, setReadError] = useState('');

    const draftOwner = useRef(null);
    const closeReview = () => { if (!submitting) onClose?.(); };
    const backReview = () => {
        if (submitting) return;
        if (selectedReview) {
            if (followUpReview) onClose?.();
            else setSelectedReview(null);
            return;
        }
        if (followUpReview) { setSelectedReview(followUpReview); return; }
        onClose?.();
    };
    // While loading there is a backdrop but no BottomSheet mounted yet.
    useBackHandler(open && !ready, closeReview);
    useEffect(() => {
        if (!open || !ready || !draftOwner.current) return;
        const { uuid, revision } = draftOwner.current;
        if (uuid !== getDeviceUuid() || revision !== getScreenCacheContextRevision()) return;
        saveReviewDraft(uuid, { category, content, parentReviewId: followUpReview?.id, followUpContext: followUpReview });
    }, [open, ready, category, content, followUpReview]);

    const loadReviews = useCallback(async () => {
        const version = ++loadVersion.current;
        const uuid = getDeviceUuid();
        const revision = getScreenCacheContextRevision();
        setReviewsLoading(true);
        try {
            const rows = await getMyReviews(uuid);
            if (version !== loadVersion.current || uuid !== getDeviceUuid() || revision !== getScreenCacheContextRevision()) return;
            const unreadRows = (rows || []).filter(hasUnreadReplies);
            const repliedRows = (rows || []).filter(row => getReviewReplies(row).length > 0);
            setReviews(rows || []);
            const draft = readReviewDraft(uuid);
            if (draft.parentReviewId) setFollowUpReview((rows || []).find(row => row.id === draft.parentReviewId) || draft.followUpContext || null);
            setSelectedReview(unreadRows[0] || (mode === 'reply' ? repliedRows[0] || null : null));
            onUnreadChanged?.(countUnreadReplies(rows || []));
        } catch {
            if (version !== loadVersion.current || uuid !== getDeviceUuid() || revision !== getScreenCacheContextRevision()) return;
            setReviews([]);
            setSelectedReview(null);
        } finally {
            if (version === loadVersion.current) { setReviewsLoading(false); setReady(true); }
        }
    }, [mode, onUnreadChanged]);

    useLayoutEffect(() => {
        setReady(false);
        if (!open) return;
        const uuid = getDeviceUuid();
        draftOwner.current = { uuid, revision: getScreenCacheContextRevision() };
        const draft = readReviewDraft(uuid);
        setCategory(normalizeCategory(draft.category));
        setContent(typeof draft.content === 'string' ? draft.content : '');
        setSelectedReview(null);
        setExpandedReviewId(null);
        setFollowUpReview(draft.followUpContext || null);
        setSubmitting(false);
        setReadError('');
        loadReviews();
        return () => { loadVersion.current++; };
    }, [loadReviews, open]);

    const canSubmit = category && content.trim().length > 0 && !submitting;

    const handleSubmit = () => {
        if (!canSubmit) return;
        if (draftOwner.current?.uuid !== getDeviceUuid() || draftOwner.current?.revision !== getScreenCacheContextRevision()) {
            setContent('');
            setFollowUpReview(null);
            onClose?.();
            return;
        }
        const uuid = getDeviceUuid();
        const submitCategory = normalizeCategory(followUpReview?.category || category);
        const payload = {
            category: submitCategory,
            content: content.trim(),
            parentReviewId: followUpReview?.id,
        };
        const owner = draftOwner.current;
        setSubmitting(true);
        submitReview(uuid, payload)
            .then(() => {
                if (owner !== draftOwner.current || owner?.revision !== getScreenCacheContextRevision() || owner?.uuid !== getDeviceUuid()) return;
                saveReviewDraft(uuid, null);
                setContent('');
                setFollowUpReview(null);
                onClose?.();
                onSubmitSuccess?.();
                return loadReviews();
            })
            .catch(() => onSubmitError?.())
            .finally(() => setSubmitting(false));
    };

    const handleSelectReview = (review) => {
        setExpandedReviewId(prev => prev === review.id ? null : review.id);
    };

    const handleConfirmSelectedReview = async ({ openComposer = false } = {}) => {
        if (!selectedReview || submitting) return;
        if (getReviewReplies(selectedReview).length === 0) {
            setSelectedReview(null);
            return;
        }
        if (!hasUnreadReplies(selectedReview)) {
            onClose?.();
            return;
        }

        const uuid = getDeviceUuid();
        const revision = getScreenCacheContextRevision();
        const version = loadVersion.current;
        const isCurrent = () => version === loadVersion.current && uuid === getDeviceUuid() && revision === getScreenCacheContextRevision();
        setSubmitting(true);
        setReadError('');
        try {
            await markReviewRead(uuid, selectedReview.id);
            if (!isCurrent()) return;
            const readAt = new Date().toISOString();
            const updated = {
                ...selectedReview,
                userReadAt: readAt,
                replies: getReviewReplies(selectedReview).map(reply => ({ ...reply, userReadAt: reply.userReadAt || readAt })),
            };
            const next = reviews.map(row => row.id === selectedReview.id ? updated : row);
            const unreadRows = next.filter(hasUnreadReplies);
            setReviews(next);
            onUnreadChanged?.(countUnreadReplies(next));
            if (openComposer) {
                setCategory(normalizeCategory(updated.category));
                setFollowUpReview(updated);
                setSelectedReview(null);
            } else if (unreadRows.length > 0) {
                setSelectedReview(unreadRows[0]);
            } else {
                onClose?.();
            }
        } catch {
            if (isCurrent()) setReadError(localize('답변 확인에 실패했어요. 다시 시도해 주세요.'));
        } finally {
            if (isCurrent()) setSubmitting(false);
        }
    };

    // Prepare the complete sheet before its first visible frame. The cancellable
    // backdrop provides immediate feedback without growing an empty bottom panel.
    if (open && !ready) {
        return <Backdrop open onClick={closeReview} sx={{ zIndex: 1200, color: '#ffe566' }}>
            <CircularProgress aria-label={localize("건의함 불러오는 중")} color="inherit" />
        </Backdrop>;
    }

    if (selectedReview) {
        const hasReply = getReviewReplies(selectedReview).length > 0;
        const replyCount = getReviewReplies(selectedReview).length;
        const unreadReviews = reviews.filter(hasUnreadReplies);
        const isUnreadSelected = hasReply && hasUnreadReplies(selectedReview);
        const selectedUnreadIndex = unreadReviews.findIndex(row => row.id === selectedReview.id);
        const unreadPosition = selectedUnreadIndex >= 0 ? selectedUnreadIndex + 1 : 0;
        const unreadTotal = unreadReviews.length;
        return (
            <BottomSheet open={open} onClose={closeReview} onBack={backReview} dismissDisabled={submitting} maxHeight="95vh">
                {!isUnreadSelected && (
                    <Button
                        onClick={() => setSelectedReview(null)}
                        sx={{ p: 0, minWidth: 0, color: '#777', fontWeight: 700, mb: 1.5 }}
                    >{localize("내 의견")}</Button>
                )}
                {isUnreadSelected && unreadTotal > 1 && (
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#dc2626', mb: 1 }}>{localize("답변 ")}{unreadPosition} / {unreadTotal}
                    </Typography>
                )}
                <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#1a1a1a', mb: 0.8 }}>
                    {hasReply ? localize("답변 {v0}개가 도착했어요", { v0: replyCount }) : localize("개발자가 확인 중이에요")}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: hasReply ? '#8a7000' : '#777', mb: 2 }}>
                    {localize(categoryLabel(selectedReview.category))} · {formatShortDate(selectedReview.createdAt)}
                </Typography>

                <Box sx={{
                    p: 1.2,
                    bgcolor: '#f8fafc',
                    borderRadius: 3,
                    border: '1px solid #eef2f7',
                    mb: sheetSpacing.action,
                }}>
                    <ReviewConversation review={selectedReview} />
                </Box>

                {readError && <Typography role="alert" sx={{ color: '#dc2626', mb: 1.5 }}>{readError}</Typography>}
                <Box sx={{ display: 'flex', gap: 1, ...(readAppLanguage() !== 'ko' && { flexDirection: 'column' }) }}>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        onClick={isUnreadSelected ? () => handleConfirmSelectedReview() : () => setSelectedReview(null)}
                        sx={{
                            py: 1.6, bgcolor: '#ffe566', color: '#5a4a00',
                            borderRadius: 3, fontSize: 16, fontWeight: 800,
                            boxShadow: 'none', '&:hover': { bgcolor: '#e8d040' },
                        }}
                    >
                        {isUnreadSelected && unreadTotal > 1 ? localize("다음 답변 보기") : isUnreadSelected ? localize("확인했어요") : localize("건의함으로 돌아가기")}
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        disabled={submitting}
                        onClick={isUnreadSelected
                            ? () => handleConfirmSelectedReview({ openComposer: true })
                            : () => {
                                setCategory(normalizeCategory(selectedReview.category));
                                setFollowUpReview(selectedReview);
                                setSelectedReview(null);
                            }}
                        sx={{
                            py: 1.6, borderColor: '#e5e7eb', color: '#111827',
                            borderRadius: 3, fontSize: 16, fontWeight: 800,
                            '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
                        }}
                    >{localize("추가 의견 남기기")}</Button>
                </Box>
            </BottomSheet>
        );
    }

    return (
        <BottomSheet open={open} onClose={closeReview} onBack={backReview} dismissDisabled={submitting} maxHeight="95vh">
            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#1a1a1a', mb: 2 }}>
                {followUpReview ? localize("추가 의견 남기기") : localize("서비스 건의함")}
            </Typography>

            {followUpReview ? (
                <FollowUpContext review={followUpReview} />
            ) : (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#fff7ed', borderRadius: 3 }}>
                    <Typography sx={{ fontSize: 15, color: '#8a7000', lineHeight: 1.7 }}>{localize("보내주신 건의는 개발자에게 바로 전달됩니다.")}<br />{localize("답변이 오면 홈에서 바로 알려드릴게요.")}</Typography>
                </Box>
            )}

            {!followUpReview && reviewsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} sx={{ color: '#8a7000' }} />
                </Box>
            ) : !followUpReview && reviews.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                    <SingleLineText sx={{ fontSize: 15, fontWeight: 800, color: '#1a1a1a', mb: 0.6 }}>{localize("내 의견")}</SingleLineText>
                    {reviews.slice(0, 3).map(review => {
                        const expanded = expandedReviewId === review.id;
                        const hasReply = getReviewReplies(review).length > 0;
                        return (
                            <Box key={review.id}>
                                <ReviewRow
                                    review={review}
                                    expanded={expanded}
                                    onClick={() => handleSelectReview(review)}
                                />
                                {expanded && (
                                    <Box sx={{ pt: 0.4, pb: 1.6, borderTop: '1px solid #f8fafc' }}>
                                        <Box sx={{
                                            p: 1.2,
                                            bgcolor: '#f8fafc',
                                            borderRadius: 3,
                                            border: '1px solid #eef2f7',
                                            mb: hasReply ? 1.2 : 0,
                                        }}>
                                            <ReviewConversation review={review} emptyText={localize("개발자가 확인 중이에요.")} compact />
                                        </Box>
                                        {hasReply && (
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => {
                                                    setCategory(normalizeCategory(review.category));
                                                    setFollowUpReview(review);
                                                    setExpandedReviewId(null);
                                                }}
                                                sx={{
                                                    py: 1.25,
                                                    borderColor: '#e5e7eb',
                                                    color: '#111827',
                                                    borderRadius: 3,
                                                    fontSize: 15,
                                                    fontWeight: 800,
                                                    '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
                                                }}
                                            >{localize("추가 의견 남기기")}</Button>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {!followUpReview && (
                <>
                    <SingleLineText sx={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', mb: 1.5 }}>{localize("어떤 건의인가요?")}</SingleLineText>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', mb: 2.5, overflowX: 'auto', pb: 0.5 }}>
                        {CATEGORIES.map((cat) => {
                            const Icon = cat.icon;
                            const selected = category === cat.value;
                            return (
                                <Box
                                    key={cat.value}
                                    onClick={() => { if (!submitting) setCategory(cat.value); }}
                                    aria-disabled={submitting}
                                    role="button"
                                    tabIndex={0}
                                    data-tappable="true"
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        px: 1.5, py: 1, borderRadius: '20px',
                                        cursor: 'pointer', flexShrink: 0,
                                        bgcolor: selected ? cat.color : 'white',
                                        border: selected ? 'none' : '1px solid #e0e0e0',
                                        transition: 'all 0.2s ease',
                                        WebkitTapHighlightColor: 'transparent',
                                        '&:active': { transform: 'scale(0.97)' },
                                    }}
                                >
                                    <Icon sx={{ fontSize: 18, color: selected ? 'white' : cat.color }} />
                                    <SingleLineText sx={{
                                        fontSize: 15, fontWeight: 700,
                                        color: selected ? 'white' : '#1a1a1a',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {localize(cat.label)}
                                    </SingleLineText>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}

            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                {followUpReview ? localize("추가 의견") : localize("건의 내용")}
            </Typography>
            <TextField
                fullWidth
                multiline
                rows={5}
                disabled={submitting}
                placeholder={followUpReview ? localize("이어 남길 의견을 작성해주세요") : localize("자유롭게 작성해주세요")}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                inputProps={{ maxLength: 2000, 'data-keyboard-lift-action': 'true' }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'white',
                    },
                }}
            />
            <Typography sx={{ fontSize: 14, color: '#777', textAlign: 'right', mt: 0.5, mb: sheetSpacing.action }}>
                {content.length} / 2000
            </Typography>

            <Button
                variant="contained"
                fullWidth
                onMouseDown={(e) => e.preventDefault()}
                disabled={!canSubmit}
                onClick={handleSubmit}
                sx={{
                    py: 1.8,
                    bgcolor: '#ffe566',
                    color: '#5a4a00',
                    borderRadius: 3,
                    fontSize: 16,
                    fontWeight: 800,
                    boxShadow: '0 4px 12px rgba(230,168,0,0.3)',
                    '&:hover': { bgcolor: '#e8d040' },
                    '&:disabled': { backgroundColor: '#ccc', color: '#777' },
                }}
            >
                {submitting ? <LoadingLabel>{localize("보내는 중...")}</LoadingLabel> : (followUpReview ? localize("추가 의견 보내기") : localize("건의하기"))}
            </Button>
        </BottomSheet>
    );
}
